// ---------------------------------------------------------------------------
// What a lead's Reply drafts card says when no Sales Representative can answer
//
// FOUND 29.10.: after a Sales Representative was deleted, the reply agent
// answered that rep's leads as a stand-in persona nobody had created. Ferenc:
// "Kein Entwurf ohne Persona". The server now writes no draft without a rep
// (entrypoints/sdr/src/server/helpers/no-sales-rep.ts), so the card must not
// say "No drafts yet." — that reads as "wait", and nothing would ever come.
//
//   - While no rep can answer the lead (GET /api/campaigns/:id/drafts lists it
//     in `leadsWithoutSalesRep`), the card says so, that nothing is written or
//     sent, and where to give the campaign a rep. Nothing to press: a draft
//     cannot be written yet.
//   - Once a rep can, a lead whose draft was skipped (`noSalesRepDraftSkippedAt`)
//     and that has none offers "Write draft". A lead with a draft has Regenerate.
//
// A quiet line like "No drafts yet.", but in the theme's own ink
// (--sdr-ink-subtle, --sdr-ink-muted) and plain Tailwind: the ratchet (R12a)
// lets no new daisyUI class or colour onto an old page. For the same reason
// "Write draft" is a text action, not a daisyUI button; it keeps the app's
// focus ring for buttons. Every class here is already in the stylesheet.
// ---------------------------------------------------------------------------

import { html, type TemplateResult } from 'lit';

/** The hint, word for word as the server's refusal (NO_SALES_REP). */
export const NO_SALES_REP = 'No Sales Representative assigned';

/** The card of a lead no Sales Representative can answer. */
export function noSalesRepNotice(): TemplateResult {
  return html`<p class="py-2 text-sm text-(--sdr-ink-subtle)" role="status">
    <span class="font-medium text-(--sdr-ink-muted)">${NO_SALES_REP}.</span>
    No draft is written for this lead’s replies, and nothing is sent, until their campaign has one: Edit campaign →
    Sales Representatives.
  </p>`;
}

/** The card of a lead whose draft was skipped, once a rep can write it. */
export function skippedDraftNotice(opts: { onWrite: () => void; busy: boolean }): TemplateResult {
  return html`<div class="flex items-center justify-between gap-2 py-2" role="status">
    <p class="text-sm text-(--sdr-ink-subtle)">No draft was written while this lead had no Sales Representative.</p>
    <button type="button" class="text-sm font-medium text-(--sdr-ink-muted) underline disabled:opacity-50" ?disabled=${opts.busy} @click=${opts.onWrite}>Write draft</button>
  </div>`;
}
